import { useEffect, useRef, useState } from 'react';

export interface UseDefferedLoadingOptions {
	/**
	 * Delay before showing the fallback UI (in milliseconds)
	 * @default 300
	 */
	delay?: number;

	/**
	 * Minimum duration to keep the fallback UI visible once shown (in milliseconds)
	 * @default 200
	 */
	minDuration?: number;
}

/**
 * Hook that delays showing a fallback UI until after a delay,
 * and keeps it visible for a minimum duration to avoid flicker.
 *
 * @param isLoading - Whether the operation is currently loading
 * @param options - Configuration options for delay and minimum duration
 * @returns Whether the fallback UI should be shown
 *
 * @example
 * ```tsx
 * const showFallback = useDefferedLoading(isLoading, { delay: 250, minDuration: 200 });
 * return showFallback ? <Spinner /> : <Content />;
 * ```
 */
export function useDefferedLoading(
	isLoading: boolean,
	options: UseDefferedLoadingOptions = {},
): boolean {
	const { delay = 300, minDuration = 200 } = options;
	const [showLoader, setShowLoader] = useState(false);
	const timeStamp = useRef<number | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: delay and minDuration are not dependencies of the effect
	useEffect(() => {
		if (!isLoading && !timeStamp.current) {
			return;
		}
		const visibilityDuration = isLoading
			? 0
			: Date.now() - (timeStamp.current || 0);
		const timeout = setTimeout(
			() => {
				timeStamp.current = isLoading ? Date.now() : null;
				setShowLoader(isLoading);
			},
			isLoading ? delay : Math.max(minDuration - visibilityDuration, 0),
		);

		return () => clearTimeout(timeout);
	}, [isLoading]);

	return showLoader;
}
